import React from 'react';
import MainLayout from '../components/Layout/MainLayout';
import { Mail, Globe, MapPin, Clock, Info as InfoIcon, Shield, Leaf, Linkedin, Youtube } from 'lucide-react';

const ContactSupport: React.FC = () => {
  const contactInfo = [
    {
      icon: Mail,
      title: 'Email Support',
      value: 'contact@growth-energy.fr',
      description: 'General inquiries',
      action: () => window.open('mailto:contact@growth-energy.fr')
    },
    {
      icon: Globe,
      title: 'Website',
      value: 'www.growth-energy.fr',
      description: 'Visit our official website',
      action: () => window.open('https://www.growth-energy.fr', '_blank')
    }
  ];

  const socialContacts = [
    {
      icon: Linkedin,
      title: 'LinkedIn',
      value: 'Growth Energy',
      action: () => window.open('https://linkedin.com/company/growthenergy0', '_blank'),
      color: 'bg-blue-700 hover:bg-blue-800'
    },
    {
      icon: Youtube,
      title: 'YouTube',
      value: '@GrowthEnergy1',
      action: () => window.open('https://youtube.com/@GrowthEnergy1', '_blank'),
      color: 'bg-red-600 hover:bg-red-700'
    }
  ];

  const officeHours = [
    { day: 'Monday - Friday', hours: '8:00 AM - 6:00 PM' },
    { day: 'Saturday', hours: '9:00 AM - 2:00 PM' },
    { day: 'Sunday', hours: 'Emergency only' }
  ];

  return (
    <MainLayout title="Contact & Support">
      <div className="p-4 space-y-6 pb-20">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <h2 className="text-xl font-semibold mb-2">Get in Touch</h2>
          <p className="text-blue-100">We're here to help you with all your energy needs</p>
        </div>

        {/* Contact Methods */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Contact Information</h3>
          <div className="space-y-4">
            {contactInfo.map((contact, index) => {
              const Icon = contact.icon;
              return (
                <button
                  key={index}
                  onClick={contact.action}
                  className="w-full flex items-center space-x-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left"
                >
                  <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/20 rounded-lg flex items-center justify-center">
                    <Icon className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 dark:text-white">{contact.title}</p>
                    <p className="text-blue-600 dark:text-blue-400 font-medium">{contact.value}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{contact.description}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Social Media */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Connect With Us</h3>
          <div className="grid grid-cols-1 gap-4">
            {socialContacts.map((social, index) => {
              const Icon = social.icon;
              return (
                <button
                  key={index}
                  onClick={social.action}
                  className={`w-full flex items-center space-x-4 p-4 text-white rounded-lg transition-colors ${social.color}`}
                >
                  <Icon className="w-6 h-6" />
                  <div className="text-left">
                    <p className="font-medium">{social.title}</p>
                    <p className="text-sm opacity-90">{social.value}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Office Hours */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
          <div className="flex items-center space-x-3 mb-4">
            <Clock className="w-6 h-6 text-blue-500" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Office Hours</h3>
          </div>
          <div className="space-y-2">
            {officeHours.map((schedule, index) => (
              <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700 last:border-b-0">
                <span className="font-medium text-gray-900 dark:text-white">{schedule.day}</span>
                <span className="text-gray-600 dark:text-gray-400">{schedule.hours}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Offices */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
          <div className="flex items-center space-x-3 mb-4">
            <MapPin className="w-6 h-6 text-green-500" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Our Offices</h3>
          </div>
          <div className="space-y-4">
            {/* European HQ */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <p className="font-medium text-gray-900 dark:text-white mb-1">European Headquarters</p>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Administrative: 212 Chemin de la Madrague-Ville, 13015 Marseille, France<br />
                Social Office: 48 Boulevard Saade – Quai Lazaret, 13002 Marseille, France
              </p>
            </div>
            {/* Paris */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <p className="font-medium text-gray-900 dark:text-white mb-1">Paris Office</p>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                4 Rue De Longchamp, 75116 Paris, France
              </p>
            </div>
            {/* African HQ */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <p className="font-medium text-gray-900 dark:text-white mb-1">African Headquarters</p>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                GEFI Solutions SEZ Limited, 4th Floor, North Tower<br />
                Two Rivers Finance and Innovation Center, Nairobi, Kenya
              </p>
            </div>
            {/* Tanzania */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <p className="font-medium text-gray-900 dark:text-white mb-1">Zanzibar Office & Showroom</p>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Building D11-04, Fumba Town, Zanzibar
              </p>
            </div>
            {/* Nigeria */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <p className="font-medium text-gray-900 dark:text-white mb-1">Nigeria Offices</p>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Main: 9, A-Avenue, Citec Estate, Mbora District, Abuja<br />
                Showroom: Shops A1-25 and A1-26, Dutse Ultramodern Market, Abuja
              </p>
            </div>
            {/* Burundi */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <p className="font-medium text-gray-900 dark:text-white mb-1">Burundi Offices</p>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Main: 31 Rue Mugamba, Bujumbura, Burundi<br />
                E-Mobility Station: GEM E-Mobility, Mukaza Avenue Nzero, Bujumbura
              </p>
            </div>
          </div>
        </div>

        {/* Info Banner */}
        <div className="bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl p-4">
          <div className="flex items-center space-x-2 mb-2">
            <InfoIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <h3 className="font-semibold text-gray-800 dark:text-gray-200">About Growth Energy</h3>
          </div>
          <p className="text-gray-700 dark:text-gray-300 text-sm mb-4">
            Growth Energy is Tanzania's leading smart energy management platform, providing innovative solutions 
            for residential and commercial energy consumption monitoring and optimization.
          </p>
        </div>

        {/* Services */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
          <div className="flex items-center space-x-3 mb-4">
            <Shield className="w-6 h-6 text-green-500" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Our Services</h3>
          </div>
          <div className="space-y-4">
            <div className="border-l-4 border-blue-500 pl-4">
              <h4 className="font-medium text-gray-900 dark:text-white">Smart Metering</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">Real-time energy consumption monitoring and analytics</p>
            </div>
            <div className="border-l-4 border-green-500 pl-4">
              <h4 className="font-medium text-gray-900 dark:text-white">Device Control</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">Remote control and scheduling of connected devices</p>
            </div>
            <div className="border-l-4 border-purple-500 pl-4">
              <h4 className="font-medium text-gray-900 dark:text-white">Energy Optimization</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">AI-powered recommendations for energy savings</p>
            </div>
            <div className="border-l-4 border-orange-500 pl-4">
              <h4 className="font-medium text-gray-900 dark:text-white">Billing & Payments</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">Streamlined billing and multiple payment options</p>
            </div>
          </div>
        </div>

        {/* Sustainability */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
          <div className="flex items-center space-x-3 mb-4">
            <Leaf className="w-6 h-6 text-green-500" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Sustainability Commitment</h3>
          </div>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            We're committed to promoting sustainable energy practices and reducing carbon footprints across Tanzania.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="text-2xl font-bold text-green-600">25%</span>
              </div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">Average Energy Savings</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="text-2xl font-bold text-blue-600">10K+</span>
              </div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">tons of CO₂ emissions avoided</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/20 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="text-2xl font-bold text-purple-600">100%</span>
              </div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">Renewable Energy Goal</p>
            </div>
          </div>
        </div>

        {/* App Version */}
        <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
          <div className="text-center text-gray-600 dark:text-gray-400">
            <p className="text-sm">Energy Monitoring App - Tanzania</p>
            <p className="text-xs">Version 1.0.0 | © 2025 Growth Energy Systems</p>
          </div>
        </div>

        {/* Growth Energy Logo */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-sm text-center">
          <img src={import.meta.env.VITE_APP_LOGO} alt="EMA" className="w-48 h-auto mx-auto mb-4" />
          <p className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Powered by MFG Technologies & Growth Energy</p>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Members Of Solio Group
          </p>            
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Leading the future of renewable energy management
          </p>
        </div>
      </div>
    </MainLayout>
  );
};

export default ContactSupport;
